import { Invoice, DbDocument, DbActivityLog, FakturaVAT, Product, TaxSum } from 'fakturnia-shared';
import { DateTime } from 'luxon';

/**
 * Dane demo faktury używane w guided tour (onboarding).
 * Możesz edytować te dane, aby zmienić wygląd podglądu demo.
 */

const now = DateTime.now();
const issueDate = now.toFormat('yyyy-MM-dd');
const paymentDeadline = now.plus({ days: 14 }).toFormat('yyyy-MM-dd');

export const DEMO_INVOICE_ID = 'testowa-faktura';

export const DEMO_INVOICE: Invoice = Object.assign(new Invoice(), {
  _id: DEMO_INVOICE_ID,
  userId: 'demo-user',
  documentType: 'FakturaVAT',
  invoiceName: 'Faktura VAT 1/01/2026',
  documentNumber: '1/01/2026',
  symbol: 'FV',
  documentNo: 1,
  numberingFormat: '{symbol}/{numer}/{MM}/{RRRR}',
  formattedDateNumber: '01/2026',
  currency: 'PLN',
  source: 'local' as const,
  direction: 'income' as const,

  hasGTU: false,
  hasPKWiU: false,
  showPKWiU: false,
  hasDiscount: false,
  hasComments: false,
  showKsefQrCode: false,
  showPaymentSection: true,
  showConvertedAmount: false,
  showVatExemptionBase: false,

  seller: {
    type: 'company',
    identifierType: 'nip',
    name: 'Twoja Firma Sp. z o.o.',
    nip: '1234567890',
    street: 'ul. Przykładowa 10',
    city: 'Warszawa',
    postalCode: '00-001',
    countryCode: 'PL'
  } as any,

  buyer: {
    type: 'company',
    identifierType: 'nip',
    name: 'Kontrahent Testowy S.A.',
    nip: '9876543210',
    street: 'ul. Biznesowa 5',
    city: 'Kraków',
    postalCode: '30-001',
    countryCode: 'PL'
  } as any,

  receiver: {} as any,
  hasReceiver: false,

  comments: '',
  annotations: {} as any,

  placeHeader: { show: true, value: 'Warszawa' },
  issueDateHeader: { show: true, value: issueDate },
  subHeader: { show: true, value: issueDate, valueTo: '', isRange: false, text: 'Data sprzedaży' },
  additionalPaymentDateHeader: { show: false, value: '', text: '' },

  bankAccountNumber: { show: true, value: '12 3456 7890 1234 5678 9012 3456', text: 'Numer konta', swift: '', bank: 'Bank Przykładowy', accountDescription: '', bankOwnAccountType: null },
  additionalBankAccounts: [],
  vatAccountNumber: '',
  vatAccountBank: '',
  vatAccountSwift: '',
  vatAccountDescription: '',

  paymentDeadline: { show: true, value: paymentDeadline, text: 'Termin płatności' },
  paymentMethod: { show: true, value: 6, text: 'Przelew' },
  paymentStatus: { show: true, value: 1, text: 'Wystawiono' },
  hasPaymentDescription: false,
  paymentDescription: '',

  exchangeRate: null,
  paid: '0.00',
  toPay: '1230.00',

  products: [
    new Product({
      _id: 'demo-product-1',
      name: 'Usługa programistyczna',
      unitOfMeasure: 'usł.',
      quantity: 1,
      netPrice: 1000,
      vatRate: '23',
      netValue: 1000,
      amountOfVat: 230,
      grossValue: 1230,
      gtu: '',
      pkwiu: '',
      discount: 0
    }, true)
  ],

  prepaymentIds: [],
  prepayments: [],
  correctedInvoice: null,
  correctedInvoiceId: null,
  correctionReason: '',

  netSum: 1000,
  vatSum: 230,
  grossSum: 1230,
  taxSums: [
    new TaxSum('23', 1000, 230, 1230)
  ],
  taxSettlement: [],

  signatures: {
    issuerSignature: { show: true, value: '' },
    receiverSignature: { show: true, value: '' }
  },

  receivedPayment: 'NOT_RECEIVED' as any,

  document: new FakturaVAT()
} as Partial<Invoice>) as Invoice;


export const DEMO_DOCUMENT: DbDocument = {
  _id: DEMO_INVOICE_ID,
  createdAt: now.toISO()!,
  updatedAt: now.toISO()!,
  issueDate: issueDate,
  symbol: 'FV',
  numberingFormat: '{symbol}/{numer}/{MM}/{RRRR}',
  invoiceName: 'Faktura VAT 1/01/2026',
  documentNumber: '1/01/2026',
  documentNo: 1,
  issueDateHeader: { show: true, value: issueDate },
  placeHeader: { show: true, value: 'Warszawa' },
  subHeader: { show: true, value: issueDate, text: 'Data sprzedaży' },
  additionalPaymentDateHeader: { show: false, value: '', text: '' },
  documentType: 'FakturaVAT',
  grossSum: 1230,
  netSum: 1000,
  vatSum: 230,
  seller: {
    name: 'Twoja Firma Sp. z o.o.',
    taxId: '1234567890',
    address: 'ul. Przykładowa 10',
    city: 'Warszawa',
    zip: '00-001',
    countryCode: 'PL'
  },
  buyer: {
    name: 'Kontrahent Testowy S.A.',
    taxId: '9876543210',
    address: 'ul. Biznesowa 5',
    city: 'Kraków',
    zip: '30-001',
    countryCode: 'PL'
  },
  receiver: null,
  hasReceiver: false,
  hasDiscount: false,
  products: [
    {
      name: 'Usługa programistyczna',
      unit: 'usł.',
      quantity: 1,
      netPrice: 1000,
      vatRate: '23',
      netValue: 1000,
      vatValue: 230,
      grossValue: 1230
    } as any
  ],
  prepaymentIds: [],
  correctedInvoiceId: '',
  currency: 'PLN',
  isSent: false,
  isPrinted: false,
  isDownloaded: false,
  paymentStatus: { show: true, value: 1, text: 'Wystawiono' },
  paymentMethod: { show: true, value: 6, text: 'Przelew' },
  paymentDeadline: { show: true, value: paymentDeadline, text: 'Termin płatności' },
  bankAccountNumber: { show: true, value: '12 3456 7890 1234 5678 9012 3456', text: 'Numer konta' },
  vatAccountNumber: '',
  hasPaymentDescription: false,
  paymentDescription: '',
  hasComments: false,
  comments: '',
  signatures: {
    issuerSignature: { show: true, value: { type: '' } },
    receiverSignature: { show: true, value: { type: '' } }
  },
  hasGTU: false,
  hasPKWiU: false,
  isPaid: true,
  isDeleted: false,
  source: 'local',
  direction: 'income',
  isKsefSynced: false,
  isKsefRequired: false,
  linkedDocuments: []
} as DbDocument;


export const DEMO_ACTIVITY_LOGS: DbActivityLog[] = [
  {
    _id: 'demo-log-1',
    refId: DEMO_INVOICE_ID,
    modelType: 'DOCUMENT' as any,
    userId: 'demo-user',
    logType: 'CREATE' as any,
    data: { invoiceName: 'Faktura VAT 1/01/2026' },
    isDeleted: false,
    createdAt: now.minus({ minutes: 5 }).toJSDate(),
    updatedAt: now.minus({ minutes: 5 }).toJSDate()
  } as DbActivityLog,
  {
    _id: 'demo-log-2',
    refId: DEMO_INVOICE_ID,
    modelType: 'DOCUMENT' as any,
    userId: 'demo-user',
    logType: 'UPDATE' as any,
    data: { invoiceName: 'Faktura VAT 1/01/2026', changes: ['Zmieniono status płatności'] },
    isDeleted: false,
    createdAt: now.minus({ minutes: 3 }).toJSDate(),
    updatedAt: now.minus({ minutes: 3 }).toJSDate()
  } as DbActivityLog,
  {
    _id: 'demo-log-3',
    refId: DEMO_INVOICE_ID,
    modelType: 'DOCUMENT' as any,
    userId: 'demo-user',
    logType: 'SENT_BY_EMAIL' as any,
    data: { invoiceName: 'Faktura VAT 1/01/2026', email: 'kontrahent@example.com' },
    isDeleted: false,
    createdAt: now.minus({ minutes: 1 }).toJSDate(),
    updatedAt: now.minus({ minutes: 1 }).toJSDate()
  } as DbActivityLog
];
